import React, { useEffect, useMemo, useState } from 'react';
import { Banknote, Plus, TrendingUp, TrendingDown, Scale, Loader2, Trash2, Wallet, FileDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '../lib/utils';

const METHODS = [
  { value: 'especes', label: 'Especes' },
  { value: 'virement', label: 'Virement' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'mobile_money', label: 'Mobile Money' },
];

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const AgentDeposits = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin', 'agent'].includes(user?.role);
  const [month, setMonth] = useState(currentMonth());
  const [summary, setSummary] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState({ agent_id: '', amount: '', method: 'especes', deposit_date: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([
        isAdmin ? api.get(`/agent-deposits/summary?month=${month}`) : Promise.resolve({ data: null }),
        api.get(`/agent-deposits?month=${month}`),
      ]);
      setSummary(s.data);
      setDeposits(d.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [month]);

  const agentOptions = useMemo(() => (summary?.rows || []).map(r => ({
    value: r.agent_id, label: `${r.agent_name} (${r.agent_employee_id || r.agent_role})`,
  })), [summary]);

  const openCreate = (preselectAgent = '') => {
    setForm({
      agent_id: preselectAgent,
      amount: '',
      method: 'especes',
      deposit_date: new Date().toISOString().slice(0, 10),
      notes: '',
    });
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (!form.agent_id) { toast.error('Selectionnez un agent'); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Montant invalide'); return; }
    setSaving(true);
    try {
      await api.post('/agent-deposits', {
        agent_id: form.agent_id,
        amount: parseFloat(form.amount),
        method: form.method,
        deposit_date: form.deposit_date || null,
        notes: form.notes || null,
      });
      toast.success('Versement enregistre');
      setOpenDialog(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur d\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce versement ?')) return;
    try {
      await api.delete(`/agent-deposits/${id}`);
      toast.success('Versement supprime');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de suppression');
    }
  };

  const handleDownloadReceipt = async (deposit) => {
    try {
      const res = await api.get(`/agent-deposits/${deposit.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `recu_${deposit.reference || deposit.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de telechargement');
    }
  };

  return (
    <div className="space-y-6" data-testid="agent-deposits-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#1B3A4B] flex items-center gap-2">
            <Banknote className="w-6 h-6 text-[#D4A745]" />
            Versements Agents
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Suivi mensuel des encaissements et versements des agents collecteurs.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="w-40"
            data-testid="deposits-month-input"
          />
          {isAdmin && (
            <Button
              onClick={() => openCreate()}
              className="bg-[#1B3A4B] hover:bg-[#142D3A]"
              data-testid="new-deposit-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau versement
            </Button>
          )}
        </div>
      </div>

      {/* Totals */}
      {isAdmin && summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="text-sm text-gray-500">Total encaisse</p>
                  <p className="text-2xl font-bold text-[#1B3A4B]" data-testid="total-collected">
                    {formatCurrency(summary.totals.collected, 'XOF')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <TrendingDown className="w-8 h-8 text-green-500" />
                <div>
                  <p className="text-sm text-gray-500">Total verse</p>
                  <p className="text-2xl font-bold text-[#1B3A4B]" data-testid="total-deposited">
                    {formatCurrency(summary.totals.deposited, 'XOF')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={`border-l-4 ${summary.totals.balance > 0 ? 'border-l-red-500' : 'border-l-emerald-500'}`}>
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <Scale className={`w-8 h-8 ${summary.totals.balance > 0 ? 'text-red-500' : 'text-emerald-500'}`} />
                <div>
                  <p className="text-sm text-gray-500">Solde du a verser</p>
                  <p className={`text-2xl font-bold ${summary.totals.balance > 0 ? 'text-red-600' : 'text-emerald-600'}`} data-testid="total-balance">
                    {formatCurrency(summary.totals.balance, 'XOF')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Agents table */}
      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-[#D4A745]" />
              Liste des agents depositaires - {month}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-6 h-6 animate-spin text-[#D4A745]" />
              </div>
            ) : !summary?.rows?.length ? (
              <p className="text-center text-gray-500 py-8">Aucun encaissement ni versement ce mois-ci.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agent</TableHead>
                    <TableHead className="text-right">Encaisse</TableHead>
                    <TableHead className="text-right">Verse</TableHead>
                    <TableHead className="text-right">Solde du</TableHead>
                    <TableHead className="w-24"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summary.rows.map((r) => (
                    <TableRow key={r.agent_id} data-testid={`agent-row-${r.agent_id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{r.agent_name}</p>
                          <p className="text-xs text-gray-500">
                            {r.agent_employee_id || '-'} &middot; {r.collected_count} paiement{r.collected_count > 1 ? 's' : ''}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(r.collected_amount, r.currency)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(r.deposited_amount, r.currency)}</TableCell>
                      <TableCell className="text-right">
                        {r.balance > 0 ? (
                          <Badge className="bg-red-100 text-red-800 font-mono" data-testid="balance-badge">
                            {formatCurrency(r.balance, r.currency)}
                          </Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-800 font-mono">
                            Solde
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="ghost" onClick={() => openCreate(r.agent_id)} data-testid={`quick-deposit-${r.agent_id}`}>
                          <Plus className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Deposits history */}
      <Card>
        <CardHeader>
          <CardTitle>Historique des versements - {month}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-[#D4A745]" />
            </div>
          ) : deposits.length === 0 ? (
            <p className="text-center text-gray-500 py-8">Aucun versement enregistre ce mois-ci.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Agent</TableHead>
                  <TableHead>Methode</TableHead>
                  <TableHead className="text-right">Montant</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="w-28">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deposits.map((d) => (
                  <TableRow key={d.id} data-testid={`deposit-row-${d.id}`}>
                    <TableCell>{formatDate(d.deposit_date)}</TableCell>
                    <TableCell className="font-mono text-xs">{d.reference}</TableCell>
                    <TableCell>{d.agent_name || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{METHODS.find(m => m.value === d.method)?.label || d.method}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold">
                      {formatCurrency(d.amount, d.currency)}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600 max-w-xs truncate">{d.notes || '-'}</TableCell>
                    <TableCell className="w-28">
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleDownloadReceipt(d)} title="Reçu PDF" className="text-blue-600 hover:text-blue-800" data-testid={`download-receipt-${d.id}`}>
                          <FileDown className="w-4 h-4" />
                        </Button>
                        {isAdmin && (
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(d.id)} className="text-red-500 hover:text-red-700">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enregistrer un versement</DialogTitle>
            <DialogDescription>
              Saisissez un versement que l'agent a remis a l'agence.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Agent depositaire *</Label>
              <Select value={form.agent_id} onValueChange={(v) => setForm(f => ({ ...f, agent_id: v }))}>
                <SelectTrigger data-testid="deposit-agent-select"><SelectValue placeholder="Selectionnez un agent" /></SelectTrigger>
                <SelectContent>
                  {agentOptions.length === 0 ? (
                    <div className="px-2 py-1 text-sm text-gray-500">Aucun agent collecteur ce mois</div>
                  ) : (
                    agentOptions.map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Montant (XOF) *</Label>
                <Input
                  type="number" min="0" step="100"
                  value={form.amount}
                  onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))}
                  data-testid="deposit-amount-input"
                />
              </div>
              <div>
                <Label>Methode</Label>
                <Select value={form.method} onValueChange={(v) => setForm(f => ({ ...f, method: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Date du versement</Label>
              <Input
                type="date"
                value={form.deposit_date}
                onChange={(e) => setForm(f => ({ ...f, deposit_date: e.target.value }))}
              />
            </div>
            <div>
              <Label>Notes (optionnel)</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(false)}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="save-deposit-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgentDeposits;
